import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfigView } from '../api/generated/models/ConfigView';
import { UserConfigEntryView } from '../api/generated/models/UserConfigEntryView';
import Pagination from '../components/Pagination';
import Modal from '../components/ui/Modal';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { usePermissions } from '../hooks/usePermissions';
import {
  CONTENT_MASTER_ON_DEMAND_LIMIT_CONFIG,
  fetchContentMasterAiHistory,
  fetchContentMasterAiTokens,
  putContentMasterUserConfig,
} from '../store/slices/contentMasterSlice';
import { addNotification } from '../store/slices/notificationSlice';
import { helper } from '../utils/helper';

/** Rough tokens per one full product generation; UI estimate only (not from API). */
const CONTENT_MASTER_APPROX_TOKENS_PER_PRODUCT = 2500;

/** Locale date + time for a history row (`createdAt` is ISO); em dash when missing/invalid. */
const formatHistoryDate = (iso?: string | null): string => {
  if (!iso) {
    return '-';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
};

/**
 * Convert an integer-cents string (as stored in user_config.content_master_on_demand_limit)
 * to a fixed-2 euros string ('' when input is empty or not a valid non-negative integer).
 */
const centsToEurosString = (cents: string): string => {
  if (!cents) return '';
  if (!/^\d+$/.test(cents.trim())) return '';
  const n = parseInt(cents.trim(), 10);
  if (!Number.isFinite(n) || n < 0) return '';
  return (n / 100).toFixed(2);
};

/**
 * Parse a euros string (accepts '.' or ',' as decimal separator, up to 2 fraction digits).
 * Returns the equivalent integer cents, or null when the input is not a valid non-negative amount.
 */
const eurosStringToCents = (euros: string): number | null => {
  const v = euros.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return null;
  const cents = Math.round(parseFloat(v) * 100);
  if (!Number.isFinite(cents) || cents < 0) return null;
  return cents;
};

const ContentMasterUsagePage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // On-demand limits are the parent user's call: the whole card is hidden
  // for subusers (owner-only billing setting).
  const { isSubuser } = usePermissions();

  const {
    aiTokensSummary,
    aiTokensLoading,
    aiTokensError,
    aiHistoryItems,
    aiHistoryTotal,
    aiHistoryLoading,
    aiHistoryError,
    userConfigSaving,
    userConfigError,
    workspaceConfig,
    wawiLanguagesError,
  } = useAppSelector((state) => state.contentMaster);
  const contact = useAppSelector((state) => state.profile.contact);
  const subscriptions = useAppSelector((state) => state.licenses.items);

  // Active Content Master subscription drives the change/upgrade CTA. Tier is
  // derived from the product name (Starter / Pro / Ultimate), mirroring the
  // logic used on the license cards. Ultimate is the top tier: nothing to
  // upgrade to, so its CTA moves to a low-key footer link.
  const activeContentMasterProductName = useMemo(() => {
    const sub = subscriptions.find(
      (s) => s.pluginName === 'content_master' && helper.isSubscriptionActive(s)
    );
    return sub?.productName ?? '';
  }, [subscriptions]);
  const isUltimatePlan = /ultimate/i.test(activeContentMasterProductName);

  // Workspace config is owned by the centralized bootstrap; the page only
  // waits until it lands (or its fetch failed) before firing tokens / history.
  const cmInit = workspaceConfig != null || wawiLanguagesError != null;
  const [histPage, setHistPage] = useState(1);
  const [histLimit, setHistLimit] = useState(20);

  // On-demand limit is stored in cents on the API (user_config.content_master_on_demand_limit);
  // the UI works with euros (two decimals). Empty string means "not set".
  const savedOnDemandLimitCents =
    contact?.configs?.find((c) => c.name === UserConfigEntryView.name.CONTENT_MASTER_ON_DEMAND_LIMIT)
      ?.value ?? '';
  const savedOnDemandLimitEuros = useMemo(
    () => centsToEurosString(savedOnDemandLimitCents),
    [savedOnDemandLimitCents]
  );
  const [onDemandLimitDraft, setOnDemandLimitDraft] = useState(savedOnDemandLimitEuros);

  useEffect(() => {
    setOnDemandLimitDraft(savedOnDemandLimitEuros);
  }, [savedOnDemandLimitEuros]);

  useEffect(() => {
    if (!cmInit) {
      return;
    }
    dispatch(fetchContentMasterAiTokens(undefined));
  }, [cmInit, dispatch]);

  useEffect(() => {
    if (!cmInit) {
      return;
    }
    dispatch(
      fetchContentMasterAiHistory({
        page: histPage,
        limit: histLimit,
      })
    );
  }, [cmInit, dispatch, histPage, histLimit]);

  const prevHistPageRef = useRef(histPage);
  useEffect(() => {
    if (prevHistPageRef.current !== histPage) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      prevHistPageRef.current = histPage;
    }
  }, [histPage]);

  useEffect(() => {
    if (!wawiLanguagesError) {
      return;
    }
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: wawiLanguagesError,
      })
    );
  }, [dispatch, t, wawiLanguagesError]);

  useEffect(() => {
    if (!aiTokensError) {
      return;
    }
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: aiTokensError,
      })
    );
  }, [aiTokensError, dispatch, t]);

  useEffect(() => {
    if (!aiHistoryError) {
      return;
    }
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: aiHistoryError,
      })
    );
  }, [aiHistoryError, dispatch, t]);

  useEffect(() => {
    if (!userConfigError) {
      return;
    }
    dispatch(
      addNotification({
        type: 'error',
        title: t('error'),
        message: userConfigError,
      })
    );
  }, [dispatch, t, userConfigError]);

  const allowedTokens = aiTokensSummary?.totalAllowedTokens;
  const tokensRemaining = aiTokensSummary?.tokensRemaining;
  const overageTokens = aiTokensSummary?.overageTokens ?? 0;
  const onDemandTokens = aiTokensSummary?.onDemandLimitTokens ?? 0;

  // Tokens consumed in the CURRENT billing period. The backend doesn't expose usageInPeriod
  // directly, but it can be reconstructed from the three period-scoped fields:
  //   usageInPeriod = (totalAllowedTokens − tokensRemaining) + overageTokens
  // This matches the same formula used server-side for generate-time quota checks and
  // for the on-demand min-value validator (see ContentMasterAiTokensSummaryView in openapi.yaml).
  // `aiTokensSummary.internalTokens` is NOT used here because, without dateFrom/dateTo filters,
  // it represents all-time usage - which is explicitly not what this card shows.
  const usedTokens = useMemo(() => {
    if (allowedTokens == null || tokensRemaining == null) {
      // No active Content Master subscription → no period to anchor against.
      return null;
    }
    return Math.max(0, allowedTokens - tokensRemaining) + overageTokens;
  }, [allowedTokens, tokensRemaining, overageTokens]);

  // Bar denominator = subscription allowance + on-demand budget (both in tokens).
  const totalAvailableTokens = useMemo(() => {
    if (allowedTokens == null && !onDemandTokens) {
      return null;
    }
    return (allowedTokens ?? 0) + onDemandTokens;
  }, [allowedTokens, onDemandTokens]);

  const usagePercent = useMemo(() => {
    if (usedTokens == null || totalAvailableTokens == null || totalAvailableTokens <= 0) {
      return null;
    }
    return Math.min(100, (usedTokens / totalAvailableTokens) * 100);
  }, [totalAvailableTokens, usedTokens]);

  // Share of the available layer occupied by the subscription portion; the rest is on-demand.
  const subscriptionPercent = useMemo(() => {
    if (totalAvailableTokens == null || totalAvailableTokens <= 0) return 0;
    return ((allowedTokens ?? 0) / totalAvailableTokens) * 100;
  }, [allowedTokens, totalAvailableTokens]);

  const usagePercentLabel =
    usagePercent != null ? `${usagePercent.toFixed(1).replace(/\.0$/, '')}%` : '-';

  const approxProductsCapacity = useMemo(() => {
    if (totalAvailableTokens == null || totalAvailableTokens <= 0) {
      return null;
    }
    return Math.round(totalAvailableTokens / CONTENT_MASTER_APPROX_TOKENS_PER_PRODUCT);
  }, [totalAvailableTokens]);

  const historyTotalPages = useMemo(() => {
    if (histLimit <= 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(aiHistoryTotal / histLimit));
  }, [aiHistoryTotal, histLimit]);

  const historyStartItem = aiHistoryTotal === 0 ? 0 : (histPage - 1) * histLimit + 1;
  const historyEndItem = Math.min(histPage * histLimit, aiHistoryTotal);

  const handleHistoryPageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && histPage > 1) {
      setHistPage((p) => p - 1);
      return;
    }
    if (direction === 'next' && histPage < historyTotalPages) {
      setHistPage((p) => p + 1);
    }
  };

  const draftCents = useMemo(() => eurosStringToCents(onDemandLimitDraft), [onDemandLimitDraft]);
  const savedCents = savedOnDemandLimitCents ? parseInt(savedOnDemandLimitCents, 10) : null;
  const hasChanges = draftCents !== savedCents;

  // Global on-demand price (euro cents per 1000 tokens) from the public
  // configs bundled into workspace-config; null when missing/invalid.
  const onDemandPriceCentsPer1000 = useMemo(() => {
    const raw = workspaceConfig?.publicConfigs?.find(
      (c) => c.name === ConfigView.name.CONTENT_MASTER_ON_DEMAND_PRICE_CENTS_PER_1000_TOKENS
    )?.value;
    const n = raw == null ? NaN : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [workspaceConfig?.publicConfigs]);

  // Live "tokens the entered amount buys" projection, recomputed on every
  // keystroke. Floor + "≈" in the UI: the authoritative cents→tokens
  // conversion happens server-side (see onDemandLimitTokens in the summary).
  const draftTokensEstimate = useMemo(() => {
    if (draftCents == null || onDemandPriceCentsPer1000 == null) {
      return null;
    }
    return Math.floor((draftCents * 1000) / onDemandPriceCentsPer1000);
  }, [draftCents, onDemandPriceCentsPer1000]);

  // Saving is a two-step flow: the button opens a confirmation modal warning
  // that usage will be billed up to (never above) the entered limit.
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);

  const handleSaveClick = () => {
    if (draftCents === null) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('error'),
          message: t('content_master_on_demand_limit_invalid'),
        })
      );
      return;
    }
    setConfirmSaveOpen(true);
  };

  const handleConfirmSaveOnDemandLimit = async () => {
    if (draftCents === null) {
      return;
    }
    const result = await dispatch(
      putContentMasterUserConfig({
        name: CONTENT_MASTER_ON_DEMAND_LIMIT_CONFIG,
        value: String(draftCents),
      })
    );
    setConfirmSaveOpen(false);
    if (putContentMasterUserConfig.fulfilled.match(result)) {
      dispatch(
        addNotification({
          type: 'success',
          title: t('success'),
          message: t('content_master_on_demand_limit_saved'),
        })
      );
    }
  };

  return (
    <div className="gpsr-content content-master-account-page">
      <div className="content-master-account-page__header">
        <h1 className="content-master-account-page__title">{t('content_master_usage_page_title')}</h1>
        <p className="content-master-account-page__subtitle">{t('content_master_usage_page_subtitle')}</p>
      </div>

      <div className="content-master-token-usage-card">
        <div className="content-master-token-usage-card__header">
          <h2 className="content-master-token-usage-card__heading">{t('content_master_token_usage_card_title')}</h2>
          {!isUltimatePlan && (
            <button
              type="button"
              className="btn btn-primary content-master-token-usage-card__change-plan"
              onClick={() => navigate('/licenses/buy?plugin=content_master')}
            >
              {t('content_master_upgrade_plan_button')}
            </button>
          )}
        </div>
        <div className="content-master-token-usage-card__row">
          <span className="content-master-token-usage-card__label">{t('content_master_token_usage_total_label')}</span>
          <span className="content-master-token-usage-card__percent">{usagePercentLabel}</span>
        </div>
        <p className="content-master-token-usage-card__sub">
          {usedTokens != null && totalAvailableTokens != null
            ? t('content_master_token_usage_subline', {
                used: usedTokens.toLocaleString(),
                limit: totalAvailableTokens.toLocaleString(),
              })
            : t('content_master_token_usage_subline_unknown')}
        </p>
        {approxProductsCapacity != null ? (
          <p className="content-master-token-usage-card__estimate">
            {t('content_master_token_usage_products_estimate', {
              products: approxProductsCapacity.toLocaleString(),
            })}
          </p>
        ) : null}
        <div
          className="content-master-token-usage-card__track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={usagePercent != null ? Math.round(usagePercent) : undefined}
          aria-label={t('content_master_token_usage_total_label')}
        >
          {/* Bottom layer: available budget split into two hoverable segments (plan + on-demand). */}
          <div
            className="content-master-token-usage-card__fill-available content-master-token-usage-card__fill-available--plan"
            style={{
              width:
                totalAvailableTokens != null && totalAvailableTokens > 0
                  ? `${subscriptionPercent}%`
                  : '0%',
              background: '#9bb7ff',
            }}
            title={t('content_master_token_bar_plan_tooltip', {
              tokens: (allowedTokens ?? 0).toLocaleString(),
            })}
          />
          <div
            className="content-master-token-usage-card__fill-available content-master-token-usage-card__fill-available--on-demand"
            style={{
              left: `${subscriptionPercent}%`,
              width:
                totalAvailableTokens != null && totalAvailableTokens > 0
                  ? `${100 - subscriptionPercent}%`
                  : '0%',
              background: '#f3b961',
            }}
            title={t('content_master_token_bar_on_demand_tooltip', {
              tokens: onDemandTokens.toLocaleString(),
            })}
          />
          {/* Top layer: used tokens, overlayed on the available layer (non-interactive so hover hits segments below). */}
          <div
            className="content-master-token-usage-card__fill-used"
            style={{
              width: usagePercent != null ? `${usagePercent}%` : '0%',
              pointerEvents: 'none',
            }}
          />
        </div>
        <dl className="content-master-token-usage-card__metrics">
          <div>
            <dt>{t('content_master_token_metric_used')}</dt>
            <dd>{usagePercentLabel}</dd>
          </div>
          <div>
            <dt>{t('content_master_token_metric_user_on_demand')}</dt>
            <dd>{savedOnDemandLimitEuros ? `${savedOnDemandLimitEuros} €` : '-'}</dd>
          </div>
        </dl>
      </div>

      {!isSubuser && (
      <div className="content-master-on-demand-card">
        <h2 className="content-master-on-demand-card__title">{t('content_master_on_demand_limit_title')}</h2>
        <p className="content-master-on-demand-card__hint">{t('content_master_on_demand_limit_hint')}</p>
        <div className="content-master-on-demand-card__form">
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={onDemandLimitDraft}
            onChange={(e) => {
              // Allow digits, one decimal separator (',' or '.'), max 2 fraction digits.
              const raw = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
              const parts = raw.split('.');
              const next =
                parts.length > 1
                  ? `${parts[0]}.${parts.slice(1).join('').slice(0, 2)}`
                  : raw;
              setOnDemandLimitDraft(next);
            }}
            aria-label={t('content_master_on_demand_limit_title')}
          />
          <span className="content-master-on-demand-card__currency">€</span>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveClick}
            disabled={userConfigSaving || draftCents === null || !hasChanges}
          >
            {userConfigSaving ? t('loading_text') : t('content_master_on_demand_limit_save')}
          </button>
        </div>
        {/* Live projection of the entered budget in tokens; hidden when the
            price config is missing or the input is empty/invalid/zero. */}
        {draftTokensEstimate != null && draftCents != null && draftCents > 0 ? (
          <p className="content-master-on-demand-card__hint">
            {t('content_master_on_demand_tokens_estimate', {
              tokens: draftTokensEstimate.toLocaleString(),
              amount: (draftCents / 100).toFixed(2),
            })}
          </p>
        ) : null}
      </div>
      )}

      {/* Consent step before saving the on-demand limit: makes it explicit
          that usage will be billed up to - never above - the entered amount. */}
      <Modal
        isOpen={confirmSaveOpen}
        onClose={userConfigSaving ? () => undefined : () => setConfirmSaveOpen(false)}
        title=""
        hideCloseButton
        isCentered
        className="content-master-apply-warning-modal"
      >
        <div className="content-master-apply-warning-modal__inner">
          <div className="content-master-apply-warning-modal__icon" aria-hidden>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h3 className="content-master-apply-warning-modal__title">
            {t('content_master_on_demand_confirm_title')}
          </h3>

          <div className="content-master-apply-warning-modal__banner" role="alert">
            <p>
              {t('content_master_on_demand_confirm_body', {
                amount: draftCents != null ? (draftCents / 100).toFixed(2) : '',
              })}
            </p>
            {draftTokensEstimate != null && draftCents != null && draftCents > 0 ? (
              <p>
                {t('content_master_on_demand_tokens_estimate', {
                  tokens: draftTokensEstimate.toLocaleString(),
                  amount: (draftCents / 100).toFixed(2),
                })}
              </p>
            ) : null}
          </div>

          <div className="content-master-apply-warning-modal__buttons">
            <button
              type="button"
              className="content-master-apply-warning-modal__button content-master-apply-warning-modal__button--cancel"
              onClick={() => setConfirmSaveOpen(false)}
              disabled={userConfigSaving}
            >
              {t('cancelText')}
            </button>
            <button
              type="button"
              className="content-master-apply-warning-modal__button content-master-apply-warning-modal__button--confirm"
              onClick={handleConfirmSaveOnDemandLimit}
              disabled={userConfigSaving}
            >
              {userConfigSaving ? t('loading_text') : t('content_master_on_demand_limit_save')}
            </button>
          </div>
        </div>
      </Modal>

      <div className="content-master-history-card content-master-history-card--simple">
        <div className="content-master-history-card__head">
          <h2 className="content-master-history-card__title">{t('content_master_ai_history_title')}</h2>
          <div className="content-master-history-card__page-size">
            <span className="content-master-per-page-label">{t('content_master_products_per_page_label')}</span>
            <select
              className="content-master-toolbar-select content-master-toolbar-select--narrow"
              value={String(histLimit)}
              onChange={(e) => {
                setHistLimit(Number(e.target.value));
                setHistPage(1);
              }}
              aria-label={t('content_master_products_per_page_aria')}
            >
              {[10, 20, 50].map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="content-master-history-table-wrap">
          {aiHistoryLoading && (
            <div className="content-master-table-panel__overlay" aria-busy="true" aria-live="polite">
              <div className="order-sidebar-spinner" aria-label={t('loading_text')} />
            </div>
          )}
          <table className="table gpsr-table content-master-table">
            <thead>
              <tr>
                <th>{t('content_master_ai_history_col_product')}</th>
                <th>SKU</th>
                <th>{t('content_master_ai_history_col_reason')}</th>
                <th>{t('content_master_ai_history_col_tokens')}</th>
                <th>{t('content_master_ai_history_col_date')}</th>
              </tr>
            </thead>
            <tbody>
              {aiHistoryItems.map((row) => (
                <tr key={row.id || `${row.createdAt}-${row.productSku}`}>
                  <td>
                    <div className="content-master-history-product-cell">
                      <div className="content-master-history-thumb">
                        {row.mainImageUrl ? <img src={row.mainImageUrl} alt="" /> : null}
                      </div>
                      {/* Truncated title; the full name is in the hover tooltip. */}
                      <span
                        className="content-master-history-product-title"
                        title={row.productTitle || undefined}
                      >
                        {row.productTitle || '-'}
                      </span>
                    </div>
                  </td>
                  <td>{row.productSku || '-'}</td>
                  <td>{row.reason || '-'}</td>
                  <td>{row.internalTokens ?? '-'}</td>
                  <td>{formatHistoryDate(row.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="content-master-history-card__footer">
          <p className="content-master-history-card__footer-text">
            {t('content_master_history_operations_count', { count: aiHistoryTotal })}
          </p>
          {usagePercent != null ? (
            <span className="content-master-history-card__footer-badge">
              {t('content_master_history_usage_badge', { pct: usagePercentLabel })}
            </span>
          ) : null}
        </div>
        {aiHistoryTotal > 0 && (
          <Pagination
            startItem={historyStartItem}
            endItem={historyEndItem}
            total={aiHistoryTotal}
            totalPages={historyTotalPages}
            page={histPage}
            loading={aiHistoryLoading}
            handlePageChange={handleHistoryPageChange}
          />
        )}
      </div>

      {/* Ultimate is the top tier - no upgrade target - so its plan-change CTA
          is a low-key footer link (bottom-right), mirroring the delete-account link. */}
      {isUltimatePlan && (
        <div className="content-master-change-plan-footer">
          <button
            type="button"
            className="content-master-change-plan-link"
            onClick={() => navigate('/licenses/buy?plugin=content_master')}
          >
            {t('content_master_change_plan_button')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ContentMasterUsagePage;
